import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth';
import { usePermission } from '@/core/permissions';
import { useSession } from '@/stores/session';

function initials(name: string): string {
  return name
    .split(' ')
    .map(p => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function UserMenu() {
  const { t } = useTranslation(['auth', 'common']);
  const user = useSession(s => s.user);
  const { role } = usePermission();
  const { signOut } = useAuth();
  if (!user) return null;

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={t('auth:userMenu.open')}
        >
          <Avatar className="size-8">
            {user.photo ? <AvatarImage src={user.photo} alt="" /> : null}
            <AvatarFallback>{initials(displayName)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="truncate font-medium">{displayName}</span>
          <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
          {role ? (
            <span className="text-muted-foreground truncate text-xs font-normal">
              {t(`common:roles.${role}`)}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
          <LogOut />
          {t('auth:userMenu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
